import axios from 'axios';

const API_BASE_URL ='https://moustache-uae.net/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (credentials) => api.post('/login', credentials),
  logout: () => api.post('/logout'),
  me: () => api.get('/me'),
  refresh: () => api.post('/refresh'),
};

// User API
export const userAPI = {
  getAll: (params) => api.get('/users', { params }),
  getOne: (id) => api.get(`/users/${id}`),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
};

// Supplier API
export const supplierAPI = {
  getAll: (params) => api.get('/suppliers', { params }),
  getOne: (id) => api.get(`/suppliers/${id}`),
  create: (data) => api.post('/suppliers', data),
  update: (id, data) => api.put(`/suppliers/${id}`, data),
  delete: (id) => api.delete(`/suppliers/${id}`),
};

// Inventory API
export const inventoryAPI = {
  getAll: (params) => api.get('/inventory', { params }),
  getOne: (id) => api.get(`/inventory/${id}`),
  create: (data) => api.post('/inventory', data),
  update: (id, data) => api.put(`/inventory/${id}`, data),
  adjust: (id, data) => api.post(`/inventory/${id}/adjust`, data),
  lowStock: () => api.get('/inventory/low-stock'),
  delete: (id) => api.delete(`/inventory/${id}`),
};

// Category API
export const categoryAPI = {
  getAll: (params) => api.get('/categories', { params }),
  getOne: (id) => api.get(`/categories/${id}`),
  create: (data) => api.post('/categories', data),
  update: (id, data) => api.put(`/categories/${id}`, data),
  delete: (id) => api.delete(`/categories/${id}`),
};

// Purchase API
export const purchaseAPI = {
  getAll: (params) => api.get('/purchases', { params }),
  getOne: (id) => api.get(`/purchases/${id}`),
  create: (data) => api.post('/purchases', data),
  update: (id, data) => api.put(`/purchases/${id}`, data),
  delete: (id) => api.delete(`/purchases/${id}`),
};

// Customer API
export const customerAPI = {
  getAll: (params) => api.get('/customers', { params }),
  getOne: (id) => api.get(`/customers/${id}`),
  create: (data) => api.post('/customers', data),
  update: (id, data) => api.put(`/customers/${id}`, data),
  delete: (id) => api.delete(`/customers/${id}`),
};

// Sales Order API
export const salesOrderAPI = {
  getAll: (params) => api.get('/sales-orders', { params }),
  getOne: (id) => api.get(`/sales-orders/${id}`),
  create: (data) => api.post('/sales-orders', data),
  update: (id, data) => api.put(`/sales-orders/${id}`, data),
  delete: (id) => api.delete(`/sales-orders/${id}`),
  print: (id, data) => api.post(`/sales-orders/${id}/print`, data),
};

// Measurement API
export const measurementAPI = {
  getAll: (params) => api.get('/measurements', { params }),
  getOne: (id) => api.get(`/measurements/${id}`),
  getLatestByCustomer: (customerId) => api.get(`/measurements/customer/${customerId}/latest`),
  create: (data) => api.post('/measurements', data),
  update: (id, data) => api.put(`/measurements/${id}`, data),
  delete: (id) => api.delete(`/measurements/${id}`),
};

// Delivery API
export const deliveryAPI = {
  getAll: (params) => api.get('/deliveries', { params }),
  getOne: (id) => api.get(`/deliveries/${id}`),
  upcoming: (days) => api.get('/deliveries/upcoming', { params: { days } }),
  create: (data) => api.post('/deliveries', data),
  update: (id, data) => api.put(`/deliveries/${id}`, data),
  delete: (id) => api.delete(`/deliveries/${id}`),
};

// Audit Log API
export const auditLogAPI = {
  getAll: (params) => api.get('/audit-logs', { params }),
  getOne: (id) => api.get(`/audit-logs/${id}`),
  stats: () => api.get('/audit-logs/stats'),
};

// Fee API
export const feeAPI = {
  getAll: (params) => api.get('/fees', { params }),
  getOne: (id) => api.get(`/fees/${id}`),
  create: (data) => api.post('/fees', data),
  update: (id, data) => api.put(`/fees/${id}`, data),
  delete: (id) => api.delete(`/fees/${id}`),
};

// Discount API
export const discountAPI = {
  getAll: (params) => api.get('/discounts', { params }),
  getOne: (id) => api.get(`/discounts/${id}`),
  create: (data) => api.post('/discounts', data),
  update: (id, data) => api.put(`/discounts/${id}`, data),
  delete: (id) => api.delete(`/discounts/${id}`),
};

// Worker API
export const workerAPI = {
  getAll: (params) => api.get('/workers', { params }),
  getOne: (id) => api.get(`/workers/${id}`),
  create: (data) => api.post('/workers', data),
  update: (id, data) => api.patch(`/workers/${id}`, data),
  delete: (id) => api.delete(`/workers/${id}`),
  getActiveTasks: (id) => api.get(`/workers/${id}/active-tasks`),
};

// Lab API
export const labAPI = {
  getAll: (params) => api.get('/lab', { params }),
  getOne: (id) => api.get(`/lab/${id}`),
  create: (data) => api.post('/lab', data),
  update: (id, data) => api.patch(`/lab/${id}`, data),
  delete: (id) => api.delete(`/lab/${id}`),
  assignWorker: (id, data) => api.post(`/lab/${id}/assign-worker`, data),
  startWork: (id) => api.post(`/lab/${id}/start-work`),
  completeWork: (id) => api.post(`/lab/${id}/complete-work`),
  getSalesOrderTasks: (salesOrderId) => api.get(`/lab/sales-order/${salesOrderId}/tasks`),
  getWorkerWorkload: (workerId) => api.get(`/lab/worker/${workerId}/workload`),
  print: (id, data) => api.post(`/lab/${id}/print`, data),
};

// Settings API (Legacy key-value)
export const settingsAPI = {
  getAll: () => api.get('/settings'),
  upsert: (items) => api.post('/settings/upsert', { items }),
  uploadLogo: (formData) => api.post('/settings/logo', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
};

// Settings Lab Printer API (New dedicated table)
export const settingsLabPrinterAPI = {
  get: () => api.get('/settings-lab-printer'),
  update: (data) => api.put('/settings-lab-printer', data),
  uploadLogo: (formData) => api.post('/settings-lab-printer/upload-logo', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  reset: () => api.delete('/settings-lab-printer/reset'),
};

// Settings Invoice Printer API (New dedicated table)
export const settingsInvoicePrinterAPI = {
  get: () => api.get('/settings-invoice-printer'),
  update: (data) => api.put('/settings-invoice-printer', data),
  uploadLogo: (formData) => api.post('/settings-invoice-printer/upload-logo', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  reset: () => api.delete('/settings-invoice-printer/reset'),
};

// VAT API
export const vatAPI = {
  get: () => api.get('/vat'), // Get active VAT (single form)
  getAll: () => api.get('/vat/all'), // Get all VATs (list)
  getActive: () => api.get('/vat/active'), // Get active VAT for calculations
  create: (data) => api.post('/vat', data),
  update: (id, data) => api.put(`/vat/${id}`, data),
  delete: (id) => api.delete(`/vat/${id}`),
  setActive: (id) => api.post(`/vat/${id}/set-active`),
};

// Print API
export const printAPI = {
  test: (data) => api.post('/print/test', data),
};

// Accounting API
export const accountingAPI = {
  getDashboardStats: (params) => api.get('/accounting/dashboard-stats', { params }),
  getSalesTrend: (params) => api.get('/accounting/sales-trend', { params }),
  getPaymentStatusBreakdown: (params) => api.get('/accounting/payment-status-breakdown', { params }),
  getRevenueByProductType: (params) => api.get('/accounting/revenue-by-product-type', { params }),
  getSalesReport: (params) => api.get('/accounting/sales-report', { params }),
  getVATReport: (params) => api.get('/accounting/vat-report', { params }),
  getPaymentTracking: (params) => api.get('/accounting/payment-tracking', { params }),
  getFinancialSummary: (params) => api.get('/accounting/financial-summary', { params }),
  getReturnsReport: (params) => api.get('/accounting/returns-report', { params }),
};

// Returns API
export const returnsAPI = {
  getAll: (params) => api.get('/returns', { params }),
  getOne: (id) => api.get(`/returns/${id}`),
  create: (data) => api.post('/returns', data),
  update: (id, data) => api.put(`/returns/${id}`, data),
  process: (id) => api.post(`/returns/${id}/process`),
  cancel: (id) => api.post(`/returns/${id}/cancel`),
  delete: (id) => api.delete(`/returns/${id}`),
  getForSalesOrder: (salesOrderId) => api.get(`/returns/sales-order/${salesOrderId}`),
};

export default api;
